import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subscription, map, shareReplay } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { ObjectUsageListComponent } from '../object-usage-list/object-usage-list.component';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import { RenameResult } from '../../../models/object-mapping.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { serverMessage } from '../../../shared/http/server-message';
import { isValidObjectName } from '../object-names';

/** Genug Treffer fuer den Vergleich ohne Gross-/Kleinschreibung; der Server sucht nach Namensanfang. */
const NAME_CHECK_SIZE = 20;
const NAME_FORMAT = /[\s.]/;

type NameError = 'nameRequired' | 'nameFormat' | 'invalidName' | 'nameTakenLibrary' | 'nameTakenEntry';

/**
 * Umbenennen eines Objekts oder, ohne `entryId`, eines ganzen Testelements (NEX-1278, NEX-2451), Design
 * `page-objektbibliothek.jsx:142-171`: erst die Vorschau (`preview=true`), dann nach Bestaetigung ausfuehren
 * (`preview=false`). Die Vorschau geht erst raus, wenn die Namenspruefung fuer genau diesen Namen geantwortet hat.
 */
@Component({
  selector: 'app-object-rename-dialog',
  imports: [
    TranslatePipe,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatProgressSpinner,
    ConfirmDialogComponent,
    ObjectUsageListComponent,
  ],
  templateUrl: './object-rename-dialog.component.html',
  styleUrl: './object-rename-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectRenameDialogComponent {
  private readonly service = inject(ObjectMappingService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly mappingId = input.required<string>();
  readonly entryId = input<string | undefined>(undefined);
  readonly currentName = input.required<string>();
  readonly renamed = output<RenameResult>();
  readonly cancelled = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;
  readonly name = signal('');
  readonly preview = signal<RenameResult | null>(null);
  readonly previewLoading = signal(false);
  readonly applying = signal(false);
  readonly error = signal<string | null>(null);
  private readonly nameTouched = signal(false);
  /** Namen, die die Pruefung als vergeben gemeldet hat, in Kleinschreibung. */
  private readonly takenNames = signal<ReadonlySet<string>>(new Set());

  readonly isLibrary = computed(() => !this.entryId());
  readonly nameTaken = computed(() => {
    const lower = this.name().toLowerCase();
    return lower === this.currentName().toLowerCase() || this.takenNames().has(lower);
  });
  readonly nameError = computed<NameError | null>(() => {
    if (!this.nameTouched()) return null;
    const name = this.name();
    if (!name.trim()) return 'nameRequired';
    if (NAME_FORMAT.test(name)) return 'nameFormat';
    if (!isValidObjectName(name)) return 'invalidName';
    if (!this.nameTaken()) return null;
    return this.isLibrary() ? 'nameTakenLibrary' : 'nameTakenEntry';
  });
  readonly nameValid = computed(() => isValidObjectName(this.name()) && !this.nameTaken());
  readonly canApply = computed(() => this.preview() !== null && !this.previewLoading());

  private readonly nameInput = viewChild.required<ElementRef<HTMLInputElement>>('nameInput');
  private request: Subscription | null = null;
  private nameCheck: Subscription | null = null;
  /** Name, fuer den die Pruefung laeuft bzw. zuletzt geantwortet hat (auch mit Fehler). */
  private checkingName: string | null = null;
  private checkedName: string | null = null;
  private previewAfterCheck = false;
  /** Die Namen der Testelemente reichen fuer jede Pruefung; sie werden einmal geladen. */
  private libraryNames: Observable<string[]> | null = null;

  constructor() {
    afterNextRender(() => this.nameInput().nativeElement.focus());
    this.destroyRef.onDestroy(() => {
      this.request?.unsubscribe();
      this.nameCheck?.unsubscribe();
    });
  }

  onNameInput(event: Event): void {
    this.name.set((event.target as HTMLInputElement).value);
    this.request?.unsubscribe();
    this.previewAfterCheck = false;
    this.previewLoading.set(false);
    this.preview.set(null);
    this.error.set(null);
  }

  onNameBlur(): void {
    this.nameTouched.set(true);
    this.checkName();
  }

  /** Klick auf „Vorschau“ und Enter im Feld laufen beide hier durch. */
  requestPreview(): void {
    if (this.previewLoading() || this.applying()) return;
    this.nameTouched.set(true);
    if (!this.nameValid()) return;
    this.preview.set(null);
    this.error.set(null);
    this.previewLoading.set(true);
    if (this.checkedName === this.name()) {
      this.loadPreview();
      return;
    }
    this.previewAfterCheck = true;
    this.checkName();
  }

  apply(): void {
    if (!this.canApply() || this.applying()) return;
    this.applying.set(true);
    this.error.set(null);
    this.rename(false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.applying.set(false);
          this.renamed.emit(result);
        },
        error: (err: unknown) => {
          // Die Vorschau kann veraltet sein (409 bei geaendertem Index): neu anfordern lassen.
          this.preview.set(null);
          this.error.set(this.messageFor(err));
          this.applying.set(false);
        },
      });
  }

  private loadPreview(): void {
    this.request = this.rename(true).subscribe({
      next: (result) => {
        this.preview.set(result);
        this.previewLoading.set(false);
      },
      error: (err: unknown) => {
        this.error.set(this.messageFor(err));
        this.previewLoading.set(false);
      },
    });
  }

  /**
   * Ein Fehler der Pruefung sperrt nichts; der Name gilt dann als frei. Das BE vergleicht exakt, ein Doppel in
   * anderer Schreibweise (`BTN` neben `btn`) ist in diesem seltenen Fall also moeglich (A5).
   */
  private checkName(): void {
    const name = this.name();
    if (!this.nameValid() || name === this.checkingName || name === this.checkedName) return;
    this.nameCheck?.unsubscribe();
    this.checkingName = name;
    this.nameCheck = this.namesLike(name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (names) => {
          const lower = name.toLowerCase();
          if (names.some((existing) => existing.toLowerCase() === lower)) {
            this.takenNames.update((taken) => new Set([...taken, lower]));
          }
          this.onNameChecked(name);
        },
        error: () => this.onNameChecked(name),
      });
  }

  private namesLike(name: string): Observable<string[]> {
    if (!this.isLibrary()) {
      return this.service
        .entries(this.projectId(), this.mappingId(), name, 0, NAME_CHECK_SIZE)
        .pipe(map((page) => page.content.map((entry) => entry.logicalName)));
    }
    // shareReplay vergisst einen Fehlschlag; die naechste Pruefung laedt dann neu.
    this.libraryNames ??= this.service.listAll(this.projectId()).pipe(
      map((libraries) => libraries.map((library) => library.name)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.libraryNames;
  }

  private onNameChecked(name: string): void {
    this.checkingName = null;
    this.checkedName = name;
    if (!this.previewAfterCheck) return;
    this.previewAfterCheck = false;
    if (name === this.name() && this.nameValid()) {
      this.loadPreview();
    } else {
      this.previewLoading.set(false);
    }
  }

  private rename(preview: boolean) {
    return this.service.rename(this.projectId(), this.mappingId(), this.entryId(), this.name(), preview);
  }

  private messageFor(err: unknown): string {
    const fromServer = serverMessage(err);
    if (fromServer) return fromServer;
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const key =
      status === 400
        ? 'objectMappings.rename.invalidName'
        : status === 409
          ? 'objectMappings.rename.conflict'
          : 'objectMappings.rename.error';
    return this.translate.instant(key);
  }
}
